import {
  DiscardTrigger,
  Baseline,
  IElement,
  OneOfChildElements,
  StashedElement,
  VersionImports,
} from '@shared/models/project.model';
import {
  ProjectChange,
  ProjectChangeType,
} from './pipeline/update-engine.models';
import {
  getElementById,
  getParentElement,
  isElement,
} from './recursive-element.helpers';
import { createId } from './id.helpers';

/** Discarded candidate-row ids for a version (map keys; presence ⇒ discarded). */
export const getDiscardedRowIds = (version: Baseline | undefined): string[] =>
  Object.keys(version?.imports?.discardedRows ?? {});

/** Whether a candidate row is currently discarded. */
export const isRowDiscarded = (
  version: Baseline | undefined,
  rowId: string,
): boolean => Boolean(version?.imports?.discardedRows?.[rowId]);

/**
 * Candidate-row id → its discard trigger (`'user'` | `'auto'`), for the
 * DISCARDED vs AUTO-DISCARDED status split (DEV-3360). Rows that are not
 * discarded are absent from the map.
 */
export const getDiscardTriggerByRowId = (
  version: Baseline | undefined,
): Record<string, DiscardTrigger> => {
  const out: Record<string, DiscardTrigger> = {};
  for (const [rowId, data] of Object.entries(
    version?.imports?.discardedRows ?? {},
  )) {
    // Legacy records may predate the `type` field; treat them as user discards
    // so the row stays DISCARDED (the prior presence-based behaviour).
    if (data) out[rowId] = data.type ?? 'user';
  }
  return out;
};

/** Candidate-row id → stashed element for discarded rows that had one. */
export const getStashedElementsByRowId = (
  version: Baseline | undefined,
): ReadonlyMap<string, IElement> => {
  const map = new Map<string, IElement>();
  for (const [rowId, data] of Object.entries(
    version?.imports?.discardedRows ?? {},
  )) {
    if (data?.stashed) map.set(rowId, data.stashed.element);
  }
  return map;
};

/** Build the stash (element + parent) for a row's materialized element. */
const stashForElement = (
  version: Baseline,
  element: IElement,
): StashedElement => {
  const parent = getParentElement(version, element);
  return { element, parentId: parent?.id ?? version.id };
};

/**
 * Collect CSV import candidate-row ids for elements about to be removed from
 * the version tree.
 */
export const collectCandidateRowIdsForElementRemoval = (
  version: Baseline,
  elementIds: readonly string[],
): string[] => {
  const out: string[] = [];
  for (const elementId of elementIds) {
    const el = getElementById(version, elementId, false);
    if (!isElement(el)) continue;
    const rowId = el.origin?.candidateRowId;
    if (el.origin?.kind === 'csvImport' && rowId && !out.includes(rowId)) {
      out.push(rowId);
    }
  }
  return out;
};

/**
 * Lookup the project element materialized from a candidate row, if any.
 */
export const findImportElementForCandidateRow = (
  version: Baseline | undefined,
  candidateRowId: string,
): IElement | undefined => {
  if (!version) return undefined;
  for (const el of version.elements) {
    if (!isElement(el)) continue;
    const found = walkForCandidateRow(el, candidateRowId);
    if (found) return found;
  }
  return undefined;
};

const walkForCandidateRow = (
  el: IElement,
  candidateRowId: string,
): IElement | undefined => {
  if (
    el.origin?.kind === 'csvImport' &&
    el.origin.candidateRowId === candidateRowId
  ) {
    return el;
  }
  for (const child of el.elements) {
    if (!isElement(child)) continue;
    const found = walkForCandidateRow(child, candidateRowId);
    if (found) return found;
  }
  return undefined;
};

/** Empty {@link VersionImports} baseline used when a version has no imports yet. */
const emptyVersionImports: VersionImports = {
  importIds: [],
  discardedRows: {},
};

/**
 * Build a {@link ProjectChangeType.VersionImports} change from a partial patch,
 * preserving every other field of the current `imports` (importIds, drafts).
 * The version change applies as a whole-object replacement, so unspecified
 * fields must be carried through or they are wiped.
 */
export const buildVersionImportsChange = (
  version: Baseline,
  patch: Partial<VersionImports>,
): ProjectChange => ({
  type: ProjectChangeType.VersionImports,
  versionId: version.id,
  imports: {
    ...emptyVersionImports,
    ...version.imports,
    ...patch,
  },
});

/**
 * Build project changes for discarding or restoring candidate rows on the
 * Import tab (DEV-3357 / DEV-3456).
 *
 * Discard removes each materialized element from the tree but stashes it on
 * the discard record; restore re-adds it verbatim (ids regenerated) so the row
 * returns to its exact prior status. A row with no element discards/restores
 * the flag only. Rows already in the target state are left untouched so a
 * mixed multi-selection can share one intent from the clicked row.
 */
export const buildBulkDiscardCandidateRowChanges = (
  version: Baseline,
  rowIds: readonly string[],
  discard: boolean,
): ProjectChange[] => {
  if (rowIds.length === 0) return [];

  const discardedRows = { ...(version.imports?.discardedRows ?? {}) };
  const changes: ProjectChange[] = [];
  let changed = false;

  for (const rowId of rowIds) {
    const currentlyDiscarded = Boolean(discardedRows[rowId]);
    if (discard === currentlyDiscarded) continue;
    changed = true;

    if (!discard) {
      const stashed = discardedRows[rowId]?.stashed;
      delete discardedRows[rowId];
      // A stale stash (row skipped while its element stayed in the tree — a
      // proposal delete before DEV-3702) only needs the flag cleared; re-adding
      // the stash would give the row a second claimant.
      if (stashed && !findImportElementForCandidateRow(version, rowId)) {
        const parentExists = Boolean(
          getElementById(version, stashed.parentId, false),
        );
        changes.push({
          type: ProjectChangeType.ElementAdd,
          elementId: parentExists ? stashed.parentId : version.id,
          // Where the element materializes follows the caller's context like
          // any other add (proposal-scoped when a proposal is selected); only
          // the discard flag is version-wide. Deliberate — UX decision pending.
          // Fresh ids: the stashed ids may still be live elsewhere (duplicated version).
          factory: { ...stashed.element, id: createId({}, true) },
          regenerateIds: true,
          generateUniqueName: false,
        });
      }
      continue;
    }

    const element = findImportElementForCandidateRow(version, rowId);
    const stashed = element ? stashForElement(version, element) : undefined;
    if (element) {
      changes.push({
        type: ProjectChangeType.ElementRemove,
        elementId: element.id,
      });
    }
    discardedRows[rowId] = { type: 'user', stashed };
  }

  if (!changed) return [];
  changes.push(buildVersionImportsChange(version, { discardedRows }));
  return changes;
};

/**
 * Build project changes for discarding or restoring a single candidate row
 * (DEV-3357). Prefer {@link buildBulkDiscardCandidateRowChanges} for
 * multi-select.
 */
export const buildDiscardCandidateRowChanges = (
  version: Baseline,
  rowId: string,
  discard: boolean,
): ProjectChange[] =>
  buildBulkDiscardCandidateRowChanges(version, [rowId], discard);

/** One row auto-discarded at import time, with the element to stash (if any). */
export type AutoDiscardEntry = {
  rowId: string;
  /** The materialized element to stash, when the code also has a product mapping. */
  stashed?: StashedElement;
};

/**
 * Build the {@link VersionImports} change that auto-discards rows at import
 * (DEV-3360). Each row gets `type: 'auto'` plus the optional stash, so Restore
 * is lossless when the code also materialized. Rows already present in
 * `discardedRows` are left untouched. Returns `[]` when nothing is new.
 */
export const buildAutoDiscardChanges = (
  version: Baseline,
  entries: readonly AutoDiscardEntry[],
): ProjectChange[] => {
  const discardedRows = { ...(version.imports?.discardedRows ?? {}) };
  let changed = false;

  for (const { rowId, stashed } of entries) {
    if (discardedRows[rowId]) continue;
    discardedRows[rowId] = { type: 'auto', stashed };
    changed = true;
  }

  if (!changed) return [];
  return [buildVersionImportsChange(version, { discardedRows })];
};

/** Collect project element ids materialized from a CSV import file. */
export const collectElementIdsForImport = (
  version: Baseline,
  importId: string,
): string[] => {
  const out: string[] = [];
  const walk = (elements: OneOfChildElements[]): void => {
    for (const child of elements) {
      if (!isElement(child)) continue;
      if (
        child.origin?.kind === 'csvImport' &&
        child.origin.importId === importId
      ) {
        out.push(child.id);
      }
      walk(child.elements);
    }
  };
  walk(version.elements);
  return out;
};

/** Build project changes for deleting a CSV import and its materialized elements. */
export const buildDeleteImportProjectChanges = (
  version: Baseline,
  importId: string,
  candidateRowIds: readonly string[],
): ProjectChange[] => {
  const elementIds = collectElementIdsForImport(version, importId);
  const rowIdSet = new Set(candidateRowIds);
  const changes: ProjectChange[] = elementIds.map((elementId) => ({
    type: ProjectChangeType.ElementRemove,
    elementId,
  }));

  const discardedRows = Object.fromEntries(
    Object.entries(version.imports?.discardedRows ?? {}).filter(
      ([id]) => !rowIdSet.has(id),
    ),
  );
  const draftsByRowId = Object.fromEntries(
    Object.entries(version.imports?.draftsByRowId ?? {}).filter(
      ([id]) => !rowIdSet.has(id),
    ),
  );

  changes.push(
    buildVersionImportsChange(version, {
      importIds: (version.imports?.importIds ?? []).filter(
        (id) => id !== importId,
      ),
      discardedRows,
      draftsByRowId,
    }),
  );

  return changes;
};

/**
 * Append VersionImports when import elements are removed from the element list,
 * stashing each removed element so it can be restored from the Import tab.
 */
export const buildDiscardedRowIdsChangesForElementRemoval = (
  version: Baseline,
  elementIds: readonly string[],
): ProjectChange[] => {
  const discardedRows = { ...(version.imports?.discardedRows ?? {}) };
  let changed = false;

  for (const elementId of elementIds) {
    const el = getElementById(version, elementId, false);
    if (!isElement(el)) continue;
    const rowId = el.origin?.candidateRowId;
    if (el.origin?.kind !== 'csvImport' || !rowId || discardedRows[rowId]) {
      continue;
    }
    discardedRows[rowId] = {
      type: 'user',
      stashed: stashForElement(version, el),
    };
    changed = true;
  }

  if (!changed) return [];
  return [buildVersionImportsChange(version, { discardedRows })];
};
